'use client';

import React, { useState } from 'react';
import { ChevronUp, ChevronDown, Trash2, ArrowRight, TrendingUp, TrendingDown, Minus, Trophy, Mountain, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import { Toaster } from 'sonner';

type TargetStatus = 'targeting' | 'attempted' | 'kom-won';

interface TargetSegment {
  id: string;
  name: string;
  type: 'climb' | 'flat' | 'descent';
  distance: number;
  elevationGain: number;
  avgGrade: number;
  winProbability: number;
  probTrend: 'up' | 'down' | 'flat';
  probDelta: number;
  gapSeconds: number;
  prevGapSeconds: number;
  komTime: string;
  myBestTime: string;
  lastAttemptDate: string;
  attempts: number;
  status: TargetStatus;
  savedDate: string;
}

const MOCK_TARGETS: TargetSegment[] = [
  {
    id: 'tgt-001',
    name: 'Canyon Blvd Sprint',
    type: 'flat',
    distance: 1.2,
    elevationGain: 14,
    avgGrade: 1.1,
    winProbability: 91,
    probTrend: 'up',
    probDelta: 4,
    gapSeconds: 2,
    prevGapSeconds: 6,
    komTime: '2:08',
    myBestTime: '2:10',
    lastAttemptDate: 'Apr 25, 2026',
    attempts: 7,
    status: 'targeting',
    savedDate: 'Mar 12, 2026',
  },
  {
    id: 'tgt-002',
    name: 'Baseline Rd Headwind',
    type: 'flat',
    distance: 2.1,
    elevationGain: 8,
    avgGrade: 0.4,
    winProbability: 93,
    probTrend: 'up',
    probDelta: 2,
    gapSeconds: 2,
    prevGapSeconds: 4,
    komTime: '4:52',
    myBestTime: '4:54',
    lastAttemptDate: 'Apr 26, 2026',
    attempts: 12,
    status: 'targeting',
    savedDate: 'Feb 28, 2026',
  },
  {
    id: 'tgt-003',
    name: 'Flagstaff Summit Push',
    type: 'climb',
    distance: 3.8,
    elevationGain: 312,
    avgGrade: 8.2,
    winProbability: 84,
    probTrend: 'up',
    probDelta: 7,
    gapSeconds: 7,
    prevGapSeconds: 18,
    komTime: '9:44',
    myBestTime: '9:51',
    lastAttemptDate: 'Apr 22, 2026',
    attempts: 3,
    status: 'attempted',
    savedDate: 'Jan 15, 2026',
  },
  {
    id: 'tgt-004',
    name: 'Marshall Mesa Puncher',
    type: 'climb',
    distance: 1.8,
    elevationGain: 134,
    avgGrade: 7.4,
    winProbability: 86,
    probTrend: 'flat',
    probDelta: 0,
    gapSeconds: 5,
    prevGapSeconds: 5,
    komTime: '5:28',
    myBestTime: '5:33',
    lastAttemptDate: 'Apr 18, 2026',
    attempts: 2,
    status: 'targeting',
    savedDate: 'Mar 5, 2026',
  },
  {
    id: 'tgt-005',
    name: 'Green Mountain West Ridge',
    type: 'climb',
    distance: 6.1,
    elevationGain: 543,
    avgGrade: 8.9,
    winProbability: 76,
    probTrend: 'up',
    probDelta: 5,
    gapSeconds: 14,
    prevGapSeconds: 22,
    komTime: '21:05',
    myBestTime: '21:19',
    lastAttemptDate: 'Apr 10, 2026',
    attempts: 2,
    status: 'attempted',
    savedDate: 'Feb 2, 2026',
  },
  {
    id: 'tgt-006',
    name: 'Wonderland Lake Loop North',
    type: 'flat',
    distance: 3.1,
    elevationGain: 67,
    avgGrade: 2.2,
    winProbability: 100,
    probTrend: 'up',
    probDelta: 29,
    gapSeconds: 0,
    prevGapSeconds: 14,
    komTime: '7:33',
    myBestTime: '7:28',
    lastAttemptDate: 'Apr 20, 2026',
    attempts: 6,
    status: 'kom-won',
    savedDate: 'Dec 10, 2025',
  },
  {
    id: 'tgt-007',
    name: 'Valmont Descent Rip',
    type: 'descent',
    distance: 2.3,
    elevationGain: -98,
    avgGrade: -4.2,
    winProbability: 88,
    probTrend: 'down',
    probDelta: -3,
    gapSeconds: 4,
    prevGapSeconds: 3,
    komTime: '3:41',
    myBestTime: '3:45',
    lastAttemptDate: 'Apr 14, 2026',
    attempts: 5,
    status: 'targeting',
    savedDate: 'Mar 20, 2026',
  },
];

function StatusBadge({ status, onChange }: { status: TargetStatus; onChange: (s: TargetStatus) => void }) {
  const [open, setOpen] = useState(false);
  const map: Record<TargetStatus, { label: string; bg: string; text: string }> = {
    'targeting': { label: 'Targeting', bg: 'bg-blue-50', text: 'text-blue-700' },
    'attempted': { label: 'Attempted', bg: 'bg-amber-50', text: 'text-amber-700' },
    'kom-won': { label: 'KOM Won', bg: 'bg-green-50', text: 'text-green-700' },
  };
  const current = map[status];
  const options: TargetStatus[] = ['targeting', 'attempted', 'kom-won'];

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold cursor-pointer hover:opacity-80 transition-opacity ${current.bg} ${current.text}`}
      >
        {status === 'kom-won' && <Trophy size={10} />}
        {current.label}
        <ChevronDown size={9} />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 bg-white rounded-xl border border-[hsl(var(--border))] shadow-lg z-20 py-1 min-w-[130px]">
          {options.map((opt) => (
            <button
              key={`status-${opt}`}
              onClick={() => { onChange(opt); setOpen(false); }}
              className={`w-full text-left px-3 py-1.5 text-xs font-medium hover:bg-[hsl(var(--muted))] transition-colors ${map[opt].text}`}
            >
              {map[opt].label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function WinProbBadge({ prob }: { prob: number }) {
  let bg = '';
  let text = '';
  if (prob >= 80) { bg = 'bg-green-50'; text = 'text-green-700'; }
  else if (prob >= 60) { bg = 'bg-amber-50'; text = 'text-amber-700'; }
  else { bg = 'bg-red-50'; text = 'text-red-700'; }
  return (
    <span className={`text-xs font-bold font-mono tabular-nums px-2 py-0.5 rounded-full ${bg} ${text}`}>{prob}%</span>
  );
}

function ProbTrend({ trend, delta }: { trend: 'up' | 'down' | 'flat'; delta: number }) {
  if (trend === 'up') return (
    <div className="flex items-center gap-0.5 text-green-600">
      <TrendingUp size={11} />
      <span className="text-[11px] font-mono tabular-nums">+{delta}%</span>
    </div>
  );
  if (trend === 'down') return (
    <div className="flex items-center gap-0.5 text-red-500">
      <TrendingDown size={11} />
      <span className="text-[11px] font-mono tabular-nums">{delta}%</span>
    </div>
  );
  return (
    <div className="flex items-center gap-0.5 text-[hsl(var(--muted-foreground))]">
      <Minus size={11} />
      <span className="text-[11px] font-mono">—</span>
    </div>
  );
}

function GapProgress({ gap, prevGap, komTime }: { gap: number; prevGap: number; komTime: string }) {
  const maxGap = 180;
  const pct = Math.max(0, Math.min(100, ((maxGap - gap) / maxGap) * 100));
  const improved = gap < prevGap;
  const won = gap <= 0;

  return (
    <div className="space-y-1 min-w-[120px]">
      <div className="flex items-center justify-between">
        <span className={`text-xs font-mono tabular-nums font-semibold ${
          won ? 'text-green-600' : improved ? 'text-green-600' : 'text-amber-600'
        }`}>
          {won ? 'KOM!' : gap > 0 ? `+${gap}s` : `${gap}s`}
        </span>
        {!won && (
          <span className="text-[10px] text-[hsl(var(--muted-foreground))]">
            was +{prevGap}s
          </span>
        )}
      </div>
      <div className="w-full h-1.5 bg-[hsl(var(--border))] rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${won ? 'bg-green-500' : improved ? 'bg-green-400' : 'bg-amber-400'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function TypeBadge({ type }: { type: TargetSegment['type'] }) {
  const map = {
    climb: 'bg-purple-50 text-purple-700',
    flat: 'bg-sky-50 text-sky-700',
    descent: 'bg-teal-50 text-teal-700',
  };
  return (
    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase tracking-wide ${map[type]}`}>{type}</span>
  );
}

export default function TargetsTable() {
  const [targets, setTargets] = useState<TargetSegment[]>(MOCK_TARGETS);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [sortKey, setSortKey] = useState<'winProbability' | 'gapSeconds' | 'attempts' | 'lastAttemptDate'>('winProbability');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleSort = (key: typeof sortKey) => {
    if (sortKey === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  const sorted = [...targets].sort((a, b) => {
    if (sortKey === 'winProbability' || sortKey === 'gapSeconds' || sortKey === 'attempts') {
      const av = a[sortKey] as number;
      const bv = b[sortKey] as number;
      return sortDir === 'asc' ? av - bv : bv - av;
    }
    return sortDir === 'asc'
      ? a.lastAttemptDate.localeCompare(b.lastAttemptDate)
      : b.lastAttemptDate.localeCompare(a.lastAttemptDate);
  });

  const handleDelete = (id: string) => {
    setDeletingId(id);
    // Backend: DELETE /api/targets/:id
    setTimeout(() => {
      setTargets((prev) => prev.filter((t) => t.id !== id));
      setDeletingId(null);
      toast.success('Segment removed from targets', { duration: 3000 });
    }, 400);
  };

  const handleBulkDelete = () => {
    const count = selectedIds.size;
    setTargets((prev) => prev.filter((t) => !selectedIds.has(t.id)));
    setSelectedIds(new Set());
    toast.success(`${count} target${count > 1 ? 's' : ''} removed`, { duration: 3000 });
  };

  const handleStatusChange = (id: string, status: TargetStatus) => {
    setTargets((prev) => prev.map((t) => t.id === id ? { ...t, status } : t));
    const label = { targeting: 'Targeting', attempted: 'Attempted', 'kom-won': 'KOM Won' }[status];
    toast.success(`Status updated to ${label}`, { duration: 2500 });
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelectedIds(selectedIds.size === sorted.length ? new Set() : new Set(sorted.map((t) => t.id)));
  };

  const SortIcon = ({ col }: { col: typeof sortKey }) => {
    if (sortKey !== col) return <ChevronUp size={11} className="opacity-20 text-[hsl(var(--muted-foreground))]" />;
    return sortDir === 'asc'
      ? <ChevronUp size={11} className="text-[hsl(var(--primary))]" />
      : <ChevronDown size={11} className="text-[hsl(var(--primary))]" />;
  };

  if (targets.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-[hsl(var(--border))] flex flex-col items-center justify-center py-20">
        <div className="w-14 h-14 rounded-2xl bg-[hsl(var(--muted))] flex items-center justify-center mb-4">
          <Trophy size={24} className="text-[hsl(var(--muted-foreground))]" />
        </div>
        <h3 className="text-base font-bold text-[hsl(var(--foreground))] mb-1">No target segments yet</h3>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mb-5 text-center max-w-xs">
          Save segments from Discover to track your gap to KOM and log your attempts.
        </p>
        <a
          href="/segment-discovery"
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[hsl(var(--primary))] text-white text-sm font-semibold hover:bg-[#e84400] transition-colors"
        >
          <ArrowRight size={14} />
          Discover segments
        </a>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-[hsl(var(--border))] overflow-hidden">
      <Toaster position="bottom-right" richColors />

      {/* Table toolbar */}
      <div className="px-5 py-3 border-b border-[hsl(var(--border))] flex items-center justify-between">
        <p className="text-xs text-[hsl(var(--muted-foreground))]">
          <span className="font-semibold text-[hsl(var(--foreground))]">{targets.length} saved segments</span>
        </p>
        {selectedIds.size > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-[hsl(var(--muted-foreground))]">{selectedIds.size} selected</span>
            <button
              onClick={handleBulkDelete}
              className="flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-medium bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
            >
              <Trash2 size={11} />
              Remove selected
            </button>
          </div>
        )}
      </div>

      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full text-sm border-collapse min-w-[900px]">
          <thead>
            <tr className="bg-[hsl(var(--muted))] border-b border-[hsl(var(--border))]">
              <th className="w-8 px-4 py-2.5">
                <input
                  type="checkbox"
                  checked={selectedIds.size === sorted.length && sorted.length > 0}
                  onChange={toggleAll}
                  className="rounded accent-[#FC4C02]"
                />
              </th>
              <th className="px-4 py-2.5 text-left text-[10px] font-semibold text-[hsl(var(--muted-foreground))] uppercase tracking-wider">
                Segment
              </th>
              <th className="px-4 py-2.5 text-left text-[10px] font-semibold text-[hsl(var(--muted-foreground))] uppercase tracking-wider">
                Status
              </th>
              <th
                className="px-4 py-2.5 text-left text-[10px] font-semibold text-[hsl(var(--muted-foreground))] uppercase tracking-wider cursor-pointer hover:text-[hsl(var(--foreground))] select-none whitespace-nowrap"
                onClick={() => handleSort('winProbability')}
              >
                <div className="flex items-center gap-1">Win Prob <SortIcon col="winProbability" /></div>
              </th>
              <th className="px-4 py-2.5 text-left text-[10px] font-semibold text-[hsl(var(--muted-foreground))] uppercase tracking-wider whitespace-nowrap">
                Trend
              </th>
              <th
                className="px-4 py-2.5 text-left text-[10px] font-semibold text-[hsl(var(--muted-foreground))] uppercase tracking-wider cursor-pointer hover:text-[hsl(var(--foreground))] select-none whitespace-nowrap"
                onClick={() => handleSort('gapSeconds')}
              >
                <div className="flex items-center gap-1">Gap to KOM <SortIcon col="gapSeconds" /></div>
              </th>
              <th className="px-4 py-2.5 text-left text-[10px] font-semibold text-[hsl(var(--muted-foreground))] uppercase tracking-wider whitespace-nowrap">
                My Best
              </th>
              <th className="px-4 py-2.5 text-left text-[10px] font-semibold text-[hsl(var(--muted-foreground))] uppercase tracking-wider whitespace-nowrap">
                KOM Time
              </th>
              <th
                className="px-4 py-2.5 text-left text-[10px] font-semibold text-[hsl(var(--muted-foreground))] uppercase tracking-wider cursor-pointer hover:text-[hsl(var(--foreground))] select-none whitespace-nowrap"
                onClick={() => handleSort('attempts')}
              >
                <div className="flex items-center gap-1">Attempts <SortIcon col="attempts" /></div>
              </th>
              <th
                className="px-4 py-2.5 text-left text-[10px] font-semibold text-[hsl(var(--muted-foreground))] uppercase tracking-wider cursor-pointer hover:text-[hsl(var(--foreground))] select-none whitespace-nowrap"
                onClick={() => handleSort('lastAttemptDate')}
              >
                <div className="flex items-center gap-1">Last Attempt <SortIcon col="lastAttemptDate" /></div>
              </th>
              <th className="px-4 py-2.5 w-16" />
            </tr>
          </thead>
          <tbody>
            {sorted.map((tgt, idx) => (
              <tr
                key={tgt.id}
                className={`border-b border-[hsl(var(--border))] transition-all duration-300 hover:bg-[hsl(var(--muted)/0.5)] group ${
                  idx % 2 === 0 ? 'bg-white' : 'bg-[hsl(var(--muted)/0.15)]'
                } ${deletingId === tgt.id ? 'opacity-0 scale-y-95' : 'opacity-100'} ${
                  tgt.status === 'kom-won' ? 'bg-green-50/30' : ''
                } ${selectedIds.has(tgt.id) ? 'bg-[hsl(var(--primary)/0.04)]' : ''}`}
              >
                <td className="px-4 py-3">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(tgt.id)}
                    onChange={() => toggleSelect(tgt.id)}
                    className="rounded accent-[#FC4C02]"
                  />
                </td>
                <td className="px-4 py-3">
                  <div>
                    <div className="flex items-center gap-1.5 mb-0.5">
                      {tgt.status === 'kom-won' && <Trophy size={11} className="text-amber-500 shrink-0" />}
                      <p className="font-semibold text-[hsl(var(--foreground))] text-[13px]">{tgt.name}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <TypeBadge type={tgt.type} />
                      <div className="flex items-center gap-1 text-[11px] text-[hsl(var(--muted-foreground))]">
                        <Mountain size={9} />
                        <span className="font-mono">{tgt.distance} km</span>
                        {tgt.elevationGain > 0 && (
                          <span className="font-mono">· +{tgt.elevationGain}m</span>
                        )}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <StatusBadge
                    status={tgt.status}
                    onChange={(s) => handleStatusChange(tgt.id, s)}
                  />
                </td>
                <td className="px-4 py-3">
                  <WinProbBadge prob={tgt.winProbability} />
                </td>
                <td className="px-4 py-3">
                  <ProbTrend trend={tgt.probTrend} delta={tgt.probDelta} />
                </td>
                <td className="px-4 py-3">
                  <GapProgress gap={tgt.gapSeconds} prevGap={tgt.prevGapSeconds} komTime={tgt.komTime} />
                </td>
                <td className="px-4 py-3">
                  <span className="font-mono tabular-nums text-[13px] font-semibold text-[hsl(var(--foreground))]">
                    {tgt.myBestTime}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className="font-mono tabular-nums text-[13px] text-[hsl(var(--muted-foreground))]">
                    {tgt.komTime}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className="font-mono tabular-nums text-[13px] text-[hsl(var(--foreground))]">{tgt.attempts}</span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1 text-[hsl(var(--muted-foreground))]">
                    <Calendar size={10} />
                    <span className="text-[12px]">{tgt.lastAttemptDate}</span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => handleDelete(tgt.id)}
                      title="Remove from targets"
                      className="p-1.5 rounded-lg text-[hsl(var(--muted-foreground))] hover:text-red-500 hover:bg-red-50 transition-all"
                    >
                      <Trash2 size={13} />
                    </button>
                    <button
                      title="View segment on Strava"
                      className="p-1.5 rounded-lg text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))] transition-all"
                    >
                      <ArrowRight size={13} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div className="px-5 py-3 bg-[hsl(var(--muted)/0.3)] border-t border-[hsl(var(--border))] flex items-center justify-between">
        <p className="text-[11px] text-[hsl(var(--muted-foreground))]">
          {targets.filter((t) => t.status === 'kom-won').length} KOM won ·{' '}
          {targets.filter((t) => t.status === 'attempted').length} attempted ·{' '}
          {targets.filter((t) => t.status === 'targeting').length} targeting
        </p>
        <a
          href="/segment-discovery"
          className="text-[11px] font-medium text-[hsl(var(--primary))] hover:underline flex items-center gap-1"
        >
          Add more targets
          <ArrowRight size={10} />
        </a>
      </div>
    </div>
  );
}